import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowUp, ArrowDown, Save } from "lucide-react";

/**
 * Admin — Price Comparison manager. One row per product SKU. Admin creates a
 * comparison record keyed by SKU (matches whatever SKUs live in the catalog),
 * flips enabled, and adds 2-4 competitor rows.
 * Route: /admin/price-comparisons
 */

const OXBLOOD = "#4A0E17";
const GOLD    = "#C9A24B";

const EMPTY_COMP = {
  name: "",
  website_url: "",
  product_name: "",
  strength: "",
  vial_count: 3,
  total_price: 0,
  price_per_vial: null,
  testing_status: "",
  coa_available: false,
  source_notes: "",
  shipping_notes: "",
  comparison_notes: "",
  date_checked: new Date().toISOString().slice(0, 10),
  active: true,
  sort_order: 0,
};

export default function AdminPriceComparisons() {
  const [list, setList] = useState([]);
  const [selectedSku, setSelectedSku] = useState("");
  const [record, setRecord] = useState(null);
  const [newSku, setNewSku] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = async () => {
    try {
      const { data } = await api.get("/admin/price-comparisons");
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e, "Couldn't load comparisons"));
    }
  };
  useEffect(() => { reload(); }, []);

  const pick = async (sku) => {
    setSelectedSku(sku);
    try {
      const { data } = await api.get(`/admin/price-comparisons/${encodeURIComponent(sku)}`);
      setRecord({ ...data, competitors: data.competitors || [] });
    } catch {
      setRecord(null);
    }
  };

  const startNew = () => {
    if (!newSku.trim()) { toast.error("SKU required"); return; }
    const sku = newSku.trim();
    if (list.find((r) => r.product_sku === sku)) { toast.error("SKU already has a record — pick it from the list"); return; }
    setSelectedSku(sku);
    setRecord({
      product_sku: sku,
      product_name: "",
      bbk_vial_count: 3,
      bbk_total_price: 0,
      bbk_price_per_vial: null,
      enabled: true,
      competitors: [],
    });
    setNewSku("");
  };

  const save = async () => {
    if (!record) return;
    if ((record.competitors || []).length > 4) { toast.error("Max 4 competitors"); return; }
    setSaving(true);
    try {
      await api.put(`/admin/price-comparisons/${encodeURIComponent(selectedSku)}`, {
        product_id: record.product_id,
        product_name: record.product_name || "",
        bbk_vial_count: Number(record.bbk_vial_count) || 3,
        bbk_total_price: Number(record.bbk_total_price) || 0,
        bbk_price_per_vial: record.bbk_price_per_vial != null ? Number(record.bbk_price_per_vial) : null,
        enabled: !!record.enabled,
        competitors: (record.competitors || []).map((c, i) => ({
          ...c,
          vial_count: Number(c.vial_count) || 1,
          total_price: Number(c.total_price) || 0,
          price_per_vial: c.price_per_vial != null && c.price_per_vial !== "" ? Number(c.price_per_vial) : null,
          sort_order: i,
        })),
      });
      toast.success("Saved");
      await reload();
      await pick(selectedSku);
    } catch (e) {
      toast.error(formatApiError(e, "Save failed"));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!selectedSku) return;
    if (!window.confirm(`Delete comparison for ${selectedSku}?`)) return;
    try {
      await api.delete(`/admin/price-comparisons/${encodeURIComponent(selectedSku)}`);
      toast.success("Deleted");
      setSelectedSku(""); setRecord(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e, "Delete failed"));
    }
  };

  const updateComp = (i, patch) => {
    const next = [...(record.competitors || [])];
    next[i] = { ...next[i], ...patch };
    // Auto-recompute price_per_vial when total_price or vial_count changes and PPV not manually set.
    if (("total_price" in patch || "vial_count" in patch) && (next[i].price_per_vial === "" || next[i].price_per_vial == null)) {
      const vc = Number(next[i].vial_count) || 1;
      const tp = Number(next[i].total_price) || 0;
      next[i].price_per_vial = vc ? +(tp / vc).toFixed(2) : 0;
    }
    setRecord({ ...record, competitors: next });
  };

  const addComp = () => {
    if ((record.competitors || []).length >= 4) { toast.error("Max 4"); return; }
    setRecord({ ...record, competitors: [...(record.competitors || []), { ...EMPTY_COMP, sort_order: (record.competitors || []).length }] });
  };
  const removeComp = (i) => {
    const next = [...(record.competitors || [])];
    next.splice(i, 1);
    setRecord({ ...record, competitors: next });
  };
  const moveComp = (i, dir) => {
    const next = [...(record.competitors || [])];
    const j = i + dir;
    if (j < 0 || j >= next.length) return;
    [next[i], next[j]] = [next[j], next[i]];
    setRecord({ ...record, competitors: next });
  };

  return (
    <div className="min-h-screen bg-bone">
      <div className="max-w-[1200px] mx-auto px-6 lg:px-8 py-10">
        <Link to="/admin" className="inline-block font-mono uppercase tracking-[0.22em] text-[11px] mb-6" style={{ color: OXBLOOD, opacity: 0.7 }}>
          ← Back to admin
        </Link>
        <h1 className="font-sans font-bold text-[36px] leading-none tracking-[-0.01em] mb-2" style={{ color: OXBLOOD, fontFamily: "Manrope, ui-sans-serif, system-ui" }}>
          Price Comparisons
        </h1>
        <p className="text-[14px] mb-8" style={{ color: OXBLOOD, opacity: 0.7 }}>
          Manually curated apples-to-apples pricing per product SKU. Add 2–4
          competitors, keep testing/COA data honest, and buyers see the whole
          picture — including when competitors are cheaper.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6">
          {/* Sidebar list */}
          <aside className="rounded-lg border p-4" style={{ borderColor: `${OXBLOOD}22`, background: "#fff" }} data-testid="comparison-sku-list">
            <div className="mb-3 flex gap-2">
              <input
                value={newSku}
                onChange={(e) => setNewSku(e.target.value)}
                placeholder="New product SKU"
                className="flex-1 border rounded-sm px-3 py-2 text-[12px] font-mono uppercase tracking-[0.06em]"
                style={{ borderColor: `${OXBLOOD}30`, color: OXBLOOD }}
                data-testid="comparison-new-sku-input"
              />
              <button
                onClick={startNew}
                className="rounded-sm px-3 py-2 text-white font-mono uppercase tracking-[0.2em] text-[10px] inline-flex items-center gap-1"
                style={{ background: OXBLOOD }}
                data-testid="comparison-new-sku-btn"
              >
                <Plus size={12} /> New
              </button>
            </div>
            <ul className="space-y-1 max-h-[70vh] overflow-y-auto">
              {list.length === 0 && (
                <li className="text-[12px] py-2 italic" style={{ color: OXBLOOD, opacity: 0.5 }}>No comparisons yet — add one above.</li>
              )}
              {list.map((r) => (
                <li key={r.product_sku}>
                  <button
                    onClick={() => pick(r.product_sku)}
                    className={`w-full text-left rounded-sm px-3 py-2 text-[12.5px] font-mono uppercase tracking-[0.06em] transition-colors ${selectedSku === r.product_sku ? "text-white" : ""}`}
                    style={{
                      background: selectedSku === r.product_sku ? OXBLOOD : "transparent",
                      color: selectedSku === r.product_sku ? "#fff" : OXBLOOD,
                      opacity: r.enabled ? 1 : 0.5,
                    }}
                    data-testid={`comparison-sku-btn-${r.product_sku}`}
                  >
                    {r.product_sku}
                    <span className="ml-2 opacity-70 text-[10px] normal-case tracking-normal font-sans">
                      {(r.competitors || []).length} comp · {r.enabled ? "on" : "off"}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </aside>

          {/* Editor */}
          <section className="rounded-lg border p-6" style={{ borderColor: `${OXBLOOD}22`, background: "#fff" }} data-testid="comparison-editor">
            {!record ? (
              <p className="text-[13px] italic" style={{ color: OXBLOOD, opacity: 0.6 }}>Pick a SKU on the left or create a new record.</p>
            ) : (
              <div>
                {/* Top controls */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b" style={{ borderColor: `${OXBLOOD}18` }}>
                  <div>
                    <p className="font-mono uppercase tracking-[0.22em] text-[10px] mb-1" style={{ color: OXBLOOD, opacity: 0.6 }}>Editing SKU</p>
                    <p className="font-mono text-[16px] font-semibold" style={{ color: OXBLOOD }}>{selectedSku}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <label className="inline-flex items-center gap-2 text-[12.5px] cursor-pointer" style={{ color: OXBLOOD }}>
                      <input type="checkbox" checked={!!record.enabled} onChange={(e) => setRecord({ ...record, enabled: e.target.checked })} data-testid="comparison-enabled-toggle" />
                      <span className="font-mono uppercase tracking-[0.2em] text-[11px]">Enabled</span>
                    </label>
                    <button onClick={save} disabled={saving} className="rounded-sm px-4 py-2 text-white font-mono uppercase tracking-[0.22em] text-[10px] inline-flex items-center gap-1.5 disabled:opacity-60" style={{ background: OXBLOOD }} data-testid="comparison-save-btn">
                      <Save size={12} /> {saving ? "Saving…" : "Save"}
                    </button>
                    <button onClick={remove} className="rounded-sm px-3 py-2 border text-[10px] font-mono uppercase tracking-[0.22em] inline-flex items-center gap-1" style={{ borderColor: `${OXBLOOD}40`, color: OXBLOOD }} data-testid="comparison-delete-btn">
                      <Trash2 size={12} /> Delete
                    </button>
                  </div>
                </div>

                {/* BBK fields */}
                <div className="mb-6">
                  <p className="font-mono uppercase tracking-[0.22em] text-[10.5px] mb-3" style={{ color: OXBLOOD, opacity: 0.7 }}>BulkBuyKits product</p>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                    <Field label="Product name" value={record.product_name} onChange={(v) => setRecord({ ...record, product_name: v })} testid="comparison-bbk-name" />
                    <Field label="Vial count" type="number" value={record.bbk_vial_count} onChange={(v) => setRecord({ ...record, bbk_vial_count: v, bbk_price_per_vial: record.bbk_price_per_vial === null || record.bbk_price_per_vial === "" ? null : record.bbk_price_per_vial })} testid="comparison-bbk-vials" />
                    <Field label="Total price ($)" type="number" step="0.01" value={record.bbk_total_price} onChange={(v) => setRecord({ ...record, bbk_total_price: v })} testid="comparison-bbk-total" />
                    <Field label="Per vial ($, auto)" type="number" step="0.01" value={record.bbk_price_per_vial ?? +(Number(record.bbk_total_price || 0) / Number(record.bbk_vial_count || 1)).toFixed(2)} onChange={(v) => setRecord({ ...record, bbk_price_per_vial: v })} testid="comparison-bbk-ppv" />
                  </div>
                </div>

                {/* Competitors */}
                <div className="flex items-center justify-between mb-3">
                  <p className="font-mono uppercase tracking-[0.22em] text-[10.5px]" style={{ color: OXBLOOD, opacity: 0.7 }}>Competitors ({(record.competitors || []).length}/4)</p>
                  <button onClick={addComp} disabled={(record.competitors || []).length >= 4} className="rounded-sm border px-3 py-1.5 text-[10px] font-mono uppercase tracking-[0.2em] inline-flex items-center gap-1 disabled:opacity-50" style={{ borderColor: `${OXBLOOD}40`, color: OXBLOOD }} data-testid="comparison-add-comp-btn">
                    <Plus size={12} /> Add competitor
                  </button>
                </div>

                <div className="space-y-4">
                  {(record.competitors || []).length === 0 && (
                    <p className="text-[12.5px] italic" style={{ color: OXBLOOD, opacity: 0.55 }}>No competitors yet.</p>
                  )}
                  {(record.competitors || []).map((c, i) => (
                    <div key={c.id || i} className="rounded-md border p-4" style={{ borderColor: `${OXBLOOD}22`, background: `${OXBLOOD}03` }} data-testid={`comparison-comp-row-${i}`}>
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-mono uppercase tracking-[0.22em] text-[10px]" style={{ color: OXBLOOD, opacity: 0.6 }}>Competitor {i + 1}</p>
                        <div className="flex items-center gap-2">
                          <label className="inline-flex items-center gap-1.5 text-[11px] cursor-pointer" style={{ color: OXBLOOD }}>
                            <input type="checkbox" checked={!!c.active} onChange={(e) => updateComp(i, { active: e.target.checked })} data-testid={`comparison-comp-active-${i}`} />
                            <span className="font-mono uppercase tracking-[0.2em]">Active</span>
                          </label>
                          <button onClick={() => moveComp(i, -1)} disabled={i === 0} className="rounded-sm border p-1 disabled:opacity-40" style={{ borderColor: `${OXBLOOD}40`, color: OXBLOOD }}><ArrowUp size={12} /></button>
                          <button onClick={() => moveComp(i, +1)} disabled={i === (record.competitors.length - 1)} className="rounded-sm border p-1 disabled:opacity-40" style={{ borderColor: `${OXBLOOD}40`, color: OXBLOOD }}><ArrowDown size={12} /></button>
                          <button onClick={() => removeComp(i)} className="rounded-sm border p-1" style={{ borderColor: `${OXBLOOD}40`, color: OXBLOOD }}><Trash2 size={12} /></button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <Field label="Competitor name" value={c.name} onChange={(v) => updateComp(i, { name: v })} />
                        <Field label="Website URL" value={c.website_url} onChange={(v) => updateComp(i, { website_url: v })} />
                        <Field label="Product name" value={c.product_name} onChange={(v) => updateComp(i, { product_name: v })} />
                        <Field label="Strength / variant" value={c.strength} onChange={(v) => updateComp(i, { strength: v })} />
                        <Field label="Vial count" type="number" value={c.vial_count} onChange={(v) => updateComp(i, { vial_count: v })} />
                        <Field label="Total price ($)" type="number" step="0.01" value={c.total_price} onChange={(v) => updateComp(i, { total_price: v })} />
                        <Field label="Per vial ($, auto)" type="number" step="0.01" value={c.price_per_vial ?? +(Number(c.total_price || 0) / Number(c.vial_count || 1)).toFixed(2)} onChange={(v) => updateComp(i, { price_per_vial: v })} />
                        <Field label="Testing status" value={c.testing_status} onChange={(v) => updateComp(i, { testing_status: v })} placeholder="e.g. In-house HPLC, No 3rd-party" />
                        <div>
                          <label className="block font-mono uppercase tracking-[0.2em] text-[9.5px] mb-1" style={{ color: OXBLOOD, opacity: 0.6 }}>COA available</label>
                          <select value={c.coa_available ? "yes" : "no"} onChange={(e) => updateComp(i, { coa_available: e.target.value === "yes" })} className="w-full border rounded-sm px-2 py-1.5 text-[12.5px]" style={{ borderColor: `${OXBLOOD}30`, color: OXBLOOD }}>
                            <option value="no">No</option>
                            <option value="yes">Yes</option>
                          </select>
                        </div>
                        <Field label="Date checked" type="date" value={c.date_checked} onChange={(v) => updateComp(i, { date_checked: v })} />
                        <Field label="Source notes" value={c.source_notes} onChange={(v) => updateComp(i, { source_notes: v })} placeholder="Lyophilized, source, etc." />
                        <Field label="Shipping notes" value={c.shipping_notes} onChange={(v) => updateComp(i, { shipping_notes: v })} />
                      </div>
                      <div className="mt-3">
                        <label className="block font-mono uppercase tracking-[0.2em] text-[9.5px] mb-1" style={{ color: OXBLOOD, opacity: 0.6 }}>Comparison notes</label>
                        <textarea value={c.comparison_notes || ""} onChange={(e) => updateComp(i, { comparison_notes: e.target.value })} className="w-full border rounded-sm px-2 py-1.5 text-[12.5px]" style={{ borderColor: `${OXBLOOD}30`, color: OXBLOOD }} rows={2} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", step, placeholder, testid }) {
  return (
    <div>
      <label className="block font-mono uppercase tracking-[0.2em] text-[9.5px] mb-1" style={{ color: OXBLOOD, opacity: 0.6 }}>{label}</label>
      <input
        type={type}
        step={step}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full border rounded-sm px-2 py-1.5 text-[12.5px]"
        style={{ borderColor: `${OXBLOOD}30`, color: OXBLOOD }}
        data-testid={testid}
      />
    </div>
  );
}
